import { createBrowserRouter, RouterProvider } from 'react-router';

import { ErrorPage } from './routes/error';
import { LoginPage } from './routes/login';
import { ProtectedLayout } from './routes/_protected';
import { RootLayout } from './routes/_root';
import { ReposProvider } from './lib/repo-context';
import { DashboardPage, dashboardLoader } from './routes/index';
import { RoadmapPage, roadmapLoader } from './routes/roadmap';
import { NewFeaturePage, newFeatureAction, newFeatureLoader } from './routes/features.new';
import { FeaturePage, featureAction, featureLoader } from './routes/features.$id';
import { EpicPage, epicAction, epicLoader } from './routes/epics.$id';
import { SprintsPage, sprintsLoader } from './routes/sprints';
import { NewSprintPage, newSprintAction, newSprintLoader } from './routes/sprints.new';
import { SprintPage, sprintAction, sprintLoader } from './routes/sprints.$id';
import { KnowledgePage, kbIndexLoader, kbSearchAction } from './routes/knowledge';
import { NewKbPage, newKbAction } from './routes/knowledge.new';
import { KbReadPage, kbReadLoader } from './routes/knowledge.$slug';
import { EditKbPage, editKbAction, editKbLoader } from './routes/knowledge.$id.edit';
import { KbDraftsPage, kbDraftsAction, kbDraftsLoader } from './routes/knowledge.drafts';
import { SettingsPage } from './routes/settings';

/**
 * `<ReposProvider>` is mounted *inside* the route element (not at the
 * top of `<RouterProvider>`) so it has access to `useRevalidator()`.
 * Wrapping it as the root element keeps it above both the login page
 * and the protected layout — the dropdown is hidden until auth, but
 * the provider itself is cheap and harmless to mount eagerly.
 */
function RootShell() {
  return (
    <ReposProvider>
      <RootLayout />
    </ReposProvider>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootShell />,
    errorElement: <ErrorPage />,
    children: [
      { path: '/login', element: <LoginPage /> },
      {
        element: <ProtectedLayout />,
        children: [
          { index: true, element: <DashboardPage />, loader: dashboardLoader },
          { path: 'roadmap', element: <RoadmapPage />, loader: roadmapLoader },
          {
            path: 'features/new',
            element: <NewFeaturePage />,
            loader: newFeatureLoader,
            action: newFeatureAction,
          },
          {
            path: 'features/:id',
            element: <FeaturePage />,
            loader: featureLoader,
            action: featureAction,
          },
          {
            path: 'epics/:id',
            element: <EpicPage />,
            loader: epicLoader,
            action: epicAction,
          },
          { path: 'sprints', element: <SprintsPage />, loader: sprintsLoader },
          {
            path: 'sprints/new',
            element: <NewSprintPage />,
            loader: newSprintLoader,
            action: newSprintAction,
          },
          {
            path: 'sprints/:id',
            element: <SprintPage />,
            loader: sprintLoader,
            action: sprintAction,
          },
          {
            path: 'knowledge',
            element: <KnowledgePage />,
            loader: kbIndexLoader,
            action: kbSearchAction,
          },
          {
            path: 'knowledge/drafts',
            element: <KbDraftsPage />,
            loader: kbDraftsLoader,
            action: kbDraftsAction,
          },
          { path: 'knowledge/new', element: <NewKbPage />, action: newKbAction },
          {
            path: 'knowledge/:slug',
            element: <KbReadPage />,
            loader: kbReadLoader,
          },
          {
            path: 'knowledge/:id/edit',
            element: <EditKbPage />,
            loader: editKbLoader,
            action: editKbAction,
          },
          { path: 'settings', element: <SettingsPage /> },
        ],
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
